import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Info, Lock } from 'lucide-react';
import { Select } from '../ui/Field';
import { pillSecondary } from '../ui/pills';
import { ACTIONS, ACTIONS_APPLIQUEES, LIBELLES_ACTION, messageErreur, type Action, type LigneDroits } from '../../lib/equipeApi';
import { useEquipe } from './EquipeShell';
import { phraseDroits } from './droitsPhrase';

/**
 * Matrice de droits (maquette L02-03 onglet Accès, L02-04) : module × Voir · Créer · Modifier · Valider · Exporter ·
 * Données sensibles. Un modèle se pose puis s'ajuste ; les écarts avec le modèle sont entourés ET signalés par + ou −
 * (jamais la couleur seule). Chaque changement est enregistré tout de suite (regroupé sur 0,7 s) et journalisé côté serveur.
 */
interface Props {
    compteId: string;
    prenom: string;
    lignes: LigneDroits[];
    modele: string | null;
    modeles: { value: string; label: string }[];
    salairesAccordes: boolean;
    /** Le directeur a tous les droits : matrice affichée, non modifiable. */
    verrouille?: boolean;
}

type Ref = Record<string, Partial<LigneDroits>>;
// Seules les actions réellement appliquées par le serveur sont proposées : une case sans effet ferait croire à une protection inexistante.
const COLONNES = ACTIONS.filter((a) => ACTIONS_APPLIQUEES.includes(a));
const vide = (l: LigneDroits): LigneDroits => ({ ...l, voir: 0, creer: 0, modifier: 0, valider: 0, exporter: 0, sensibles: 0 });

export const MatriceDroits: React.FC<Props> = ({ compteId, prenom, lignes: initiales, modele: modeleInitial, modeles, salairesAccordes, verrouille }) => {
    const { api } = useEquipe();
    const [lignes, setLignes] = useState<LigneDroits[]>(initiales);
    const [modele, setModele] = useState<string>(modeleInitial || '');
    const [reference, setReference] = useState<Ref>({});
    const [etat, setEtat] = useState<{ type: 'attente' | 'ok' | 'erreur'; texte: string } | null>(null);
    const minuteur = useRef<number | undefined>(undefined);
    // Ne jamais écraser une saisie en attente d'enregistrement par un rechargement venu du parent (sinon la case cliquée « revient »).
    useEffect(() => { if (minuteur.current === undefined) setLignes(initiales); }, [initiales]);
    useEffect(() => () => window.clearTimeout(minuteur.current), []);

    useEffect(() => {
        if (!modele) { setReference({}); return; }
        let vivant = true;
        api.modele(modele).then((m) => { if (vivant) setReference(Object.fromEntries(m.droits.map((d) => [d.module_name as string, d]))); }).catch(() => { if (vivant) setReference({}); });
        return () => { vivant = false; };
    }, [modele, api]);

    const valeurModele = (nom: string, a: Action) => (reference[nom] ? Number(reference[nom][a] || 0) : 0);
    const ecart = (l: LigneDroits, a: Action): '+' | '−' | null => {
        if (!modele) return null;
        if (!ACTIONS_APPLIQUEES.includes(a)) return null;
        const v = Number(l[a]); const m = valeurModele(l.module_name, a);
        return v === m ? null : v > m ? '+' : '−';
    };
    const nbEcarts = useMemo(() => lignes.reduce((n, l) => n + ACTIONS.filter((a) => ecart(l, a)).length, 0), [lignes, reference, modele]); // eslint-disable-line react-hooks/exhaustive-deps

    const enregistrer = (suivantes: LigneDroits[], nomModele: string) => {
        setLignes(suivantes);
        setEtat({ type: 'attente', texte: 'Enregistrement…' });
        window.clearTimeout(minuteur.current);
        minuteur.current = window.setTimeout(async () => {
            minuteur.current = undefined;
            try { await api.enregistrerDroits(compteId, suivantes, nomModele || null); setEtat({ type: 'ok', texte: 'Enregistré : le changement prend effet immédiatement.' }); }
            catch (e) { setEtat({ type: 'erreur', texte: messageErreur(e) }); }
        }, 700);
    };

    const basculer = (nom: string, a: Action) => {
        if (verrouille) return;
        enregistrer(lignes.map((l) => {
            if (l.module_name !== nom) return l;
            const n = { ...l, [a]: l[a] ? 0 : 1 } as LigneDroits;
            if (a === 'voir' && !n.voir) return vide(n);
            if (a !== 'voir' && n[a]) n.voir = 1;
            return n;
        }), modele);
    };

    const appliquer = (nom: string) => {
        setModele(nom);
        if (!nom) return;
        api.modele(nom).then((m) => {
            const ref = Object.fromEntries(m.droits.map((d) => [d.module_name as string, d]));
            enregistrer(lignes.map((l) => (ref[l.module_name] ? ({ ...vide(l), ...ref[l.module_name] } as LigneDroits) : vide(l))), nom);
        }).catch((e) => setEtat({ type: 'erreur', texte: messageErreur(e) }));
    };

    const libelles = Object.fromEntries(lignes.map((l) => [l.module_name, l.label]));

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
                <Select aria-label="Appliquer un modèle" value={modele} disabled={verrouille} onChange={(e) => appliquer(e.target.value)} className="!w-auto min-w-[200px]">
                    <option value="">Appliquer un modèle…</option>{modeles.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </Select>
                {modele && <span className="text-[12.5px] text-content-muted">{nbEcarts === 0 ? 'Conforme au modèle' : `${nbEcarts} écart${nbEcarts > 1 ? 's' : ''} avec le modèle`}</span>}
                {modele && nbEcarts > 0 && <button type="button" className={pillSecondary(32)} onClick={() => appliquer(modele)}>Revenir au modèle</button>}
                {etat && <span role="status" className={`text-[12.5px] font-medium ml-auto ${etat.type === 'erreur' ? 'text-state-danger' : 'text-content-muted'}`}>{etat.texte}</span>}
            </div>
            {verrouille
                ? <p className="flex items-center gap-2 text-[12.5px] text-content-muted"><Lock size={13} /> Le directeur a tous les droits : cette ligne n'est pas modifiable.</p>
                : <p className="rounded-xl bg-surface-soft border border-border px-4 py-3 text-[13px] text-content"><b>En clair :</b> {phraseDroits(prenom, lignes, libelles, salairesAccordes)}</p>}
            {lignes.length === 0 ? (
                <p className="rounded-xl border border-border bg-surface px-4 py-6 text-[13px] text-content-muted">Aucun module n'est activé pour cet établissement : activez-en dans « Mes modules » pour régler des droits.</p>
            ) : (
                <div className="bg-surface border border-border rounded-xl overflow-x-auto">
                    <table className="w-full text-[12.5px] min-w-[420px]">
                        <thead>
                            <tr className="bg-surface-soft text-[10px] font-bold uppercase tracking-[.09em] text-content-muted border-b border-border">
                                <th className="text-left px-4 h-10 font-bold">Module</th>
                                {COLONNES.map((a) => <th key={a} className="px-2 text-center font-bold">{LIBELLES_ACTION[a]}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {lignes.map((l) => (
                                <tr key={l.module_name} className="border-b border-border-soft last:border-b-0">
                                    <th scope="row" className="text-left px-4 py-2 font-semibold text-content">{l.label}</th>
                                    {COLONNES.map((a) => {
                                        const e = ecart(l, a);
                                        return (
                                            <td key={a} className="text-center px-2">
                                                <label className="inline-flex items-center justify-center gap-1 min-w-[44px] min-h-[44px] md:min-h-[36px] cursor-pointer">
                                                    <input type="checkbox" checked={!!l[a]} disabled={verrouille} onChange={() => basculer(l.module_name, a)} aria-label={`${LIBELLES_ACTION[a]} — ${l.label}${e ? ` (écart avec le modèle : ${e === '+' ? 'accordé en plus' : 'retiré'})` : ''}`} className="w-4 h-4" style={{ accentColor: 'var(--acc)' }} />
                                                    {e && <span aria-hidden className="text-[11px] font-bold rounded px-1 border border-content text-content">{e}</span>}
                                                </label>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="flex items-start gap-2 text-[12px] text-content-muted"><Info size={13} className="mt-0.5 shrink-0" />
                <span><b>Voir</b> : ouvrir le module. <b>Créer</b> : ajouter des éléments. <b>Modifier</b> : changer ou supprimer. Un module non coché reste fermé à cette personne. Les salaires, le chiffre d'affaires, les coordonnées bancaires et les allergies se règlent dans l'onglet « Données sensibles ». Une case marquée + ou − s'écarte du modèle choisi.</span></p>
        </div>
    );
};
